import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, FileText, Trash2, Plus } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import api from '../../../lib/api';

const TORRE_EMILIA_ID = 'edif_74b24adaccb6';

export const EditEdificioDialog = ({ edificio, edificioForm, handleSaveEdificio, isEditEdificioOpen, saving, setEdificioForm, setIsEditEdificioOpen, onEdificioUpdated }) => {
  const [escrituraGen, setEscrituraGen] = useState((edificio?.documentacion || {}).escritura_general || null);
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const fileRef = useRef(null);

  React.useEffect(() => {
    setEscrituraGen((edificio?.documentacion || {}).escritura_general || null);
  }, [edificio?.documentacion]);

  React.useEffect(() => {
    if (isEditEdificioOpen) setActiveTab('general');
  }, [isEditEdificioOpen]);

  const isTorreEmilia = edificio?.edificio_id === TORRE_EMILIA_ID;
  const serviciosAC = edificioForm.servicios_areas_comunes || [];

  const updateServicio = (idx, key, value) => {
    const next = [...serviciosAC];
    next[idx] = { ...next[idx], [key]: value };
    setEdificioForm({ ...edificioForm, servicios_areas_comunes: next });
  };

  const addServicio = () => {
    setEdificioForm({
      ...edificioForm,
      servicios_areas_comunes: [...serviciosAC, { servicio: '', numero: '', observaciones: '' }],
    });
  };

  const removeServicio = (idx) => {
    const next = serviciosAC.filter((_, i) => i !== idx);
    setEdificioForm({ ...edificioForm, servicios_areas_comunes: next });
  };

  const handleUpload = async (file) => {
    if (!file) return;
    try {
      setBusy(true);
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/edificios/${edificio.edificio_id}/escritura-general`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEscrituraGen(res.data);
      toast.success('Escritura general cargada');
      onEdificioUpdated && onEdificioUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar el archivo');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar la escritura general de este edificio?')) return;
    try {
      setBusy(true);
      await api.delete(`/edificios/${edificio.edificio_id}/escritura-general`);
      setEscrituraGen(null);
      toast.success('Escritura general eliminada');
      onEdificioUpdated && onEdificioUpdated();
    } catch (_) {
      toast.error('No se pudo eliminar');
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/edificios/${edificio.edificio_id}/escritura-general/download`;
      const token = localStorage.getItem('token');
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('fail');
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = escrituraGen?.filename || 'escritura.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
    } catch (_) {
      toast.error('No se pudo descargar');
    }
  };

  return (
    <Dialog open={isEditEdificioOpen} onOpenChange={setIsEditEdificioOpen}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-edificio-dialog">
        <DialogHeader>
          <DialogTitle>Editar Edificio</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSaveEdificio} className="space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full grid grid-cols-4">
              <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
              <TabsTrigger value="identificacion" data-testid="tab-identificacion">Identificación</TabsTrigger>
              <TabsTrigger value="servicios" data-testid="tab-servicios">Servicios</TabsTrigger>
              <TabsTrigger value="documentacion" data-testid="tab-documentacion">Documentación</TabsTrigger>
            </TabsList>

            {/* ============== GENERAL ============== */}
            <TabsContent value="general" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input data-testid="edif-nombre" value={edificioForm.nombre} onChange={(e) => setEdificioForm({ ...edificioForm, nombre: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Denominación</Label>
                <Input data-testid="edif-denominacion" value={edificioForm.denominacion} onChange={(e) => setEdificioForm({ ...edificioForm, denominacion: e.target.value })} placeholder="Ej: TE, FC, ED1" />
                <p className="text-xs text-slate-400">Abreviatura con siglas del edificio</p>
              </div>
              <div className="space-y-2">
                <Label>Dirección *</Label>
                <Input data-testid="edif-direccion" value={edificioForm.direccion} onChange={(e) => setEdificioForm({ ...edificioForm, direccion: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea data-testid="edif-descripcion" value={edificioForm.descripcion} onChange={(e) => setEdificioForm({ ...edificioForm, descripcion: e.target.value })} rows={3} />
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={edificioForm.estado} onValueChange={(v) => setEdificioForm({ ...edificioForm, estado: v })}>
                  <SelectTrigger data-testid="edif-estado"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="inactivo">Inactivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Valor estándar cochera</Label>
                <div className="flex gap-2">
                  <Input
                    type="number" min="0" step="0.01"
                    data-testid="valor-cochera-monto"
                    placeholder="Monto"
                    value={edificioForm.valor_cochera_estandar?.monto ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '') {
                        setEdificioForm({ ...edificioForm, valor_cochera_estandar: null });
                      } else {
                        setEdificioForm({
                          ...edificioForm,
                          valor_cochera_estandar: { monto: parseFloat(val), moneda: edificioForm.valor_cochera_estandar?.moneda || 'ARS' },
                        });
                      }
                    }}
                    className="flex-1"
                  />
                  <Select
                    value={edificioForm.valor_cochera_estandar?.moneda || 'ARS'}
                    onValueChange={(v) => setEdificioForm({
                      ...edificioForm,
                      valor_cochera_estandar: { ...edificioForm.valor_cochera_estandar, monto: edificioForm.valor_cochera_estandar?.monto || 0, moneda: v },
                    })}
                  >
                    <SelectTrigger className="w-24" data-testid="valor-cochera-moneda"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ARS">ARS</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-slate-400">Valor por defecto para cocheras en contratos nuevos</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Monto Cowork Virtual (ARS)</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    data-testid="monto-cowork-virtual"
                    placeholder="Monto"
                    value={edificioForm.monto_cowork_virtual ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEdificioForm({ ...edificioForm, monto_cowork_virtual: val === '' ? null : parseFloat(val) });
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Monto Cowork Full (ARS)</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    data-testid="monto-cowork-full"
                    placeholder="Monto"
                    value={edificioForm.monto_cowork_full ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEdificioForm({ ...edificioForm, monto_cowork_full: val === '' ? null : parseFloat(val) });
                    }}
                  />
                </div>
                <p className="col-span-2 text-xs text-slate-400">Se precargan automáticamente al dar de alta un suscriptor Cowork según el tipo.</p>
              </div>
            </TabsContent>

            {/* ============== IDENTIFICACIÓN ============== */}
            <TabsContent value="identificacion" className="space-y-4 pt-4">
              <p className="text-xs text-slate-500">Padrones y nomenclatura unificados a nivel edificio (cuando aplica).</p>
              <div className="space-y-2">
                <Label>Padrón Municipal unificado</Label>
                <Input
                  data-testid="edif-padron-municipal"
                  value={edificioForm.padron_municipal_unificado || ''}
                  onChange={(e) => setEdificioForm({ ...edificioForm, padron_municipal_unificado: e.target.value })}
                  placeholder="Ej: 04-12345-6"
                />
              </div>
              <div className="space-y-2">
                <Label>Padrón Rentas unificado</Label>
                <Input
                  data-testid="edif-padron-rentas"
                  value={edificioForm.padron_rentas_unificado || ''}
                  onChange={(e) => setEdificioForm({ ...edificioForm, padron_rentas_unificado: e.target.value })}
                  placeholder="Ej: 12345/0"
                />
              </div>
              <div className="space-y-2">
                <Label>Nomenclatura catastral unificada</Label>
                <Input
                  data-testid="edif-nomenclatura"
                  value={edificioForm.nomenclatura_catastral_unificada || ''}
                  onChange={(e) => setEdificioForm({ ...edificioForm, nomenclatura_catastral_unificada: e.target.value })}
                  placeholder="Ej: 04-01-12-0012-345-000-0000"
                />
              </div>
            </TabsContent>

            {/* ============== SERVICIOS ÁREAS COMUNES ============== */}
            <TabsContent value="servicios" className="space-y-3 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold">Servicios áreas comunes</Label>
                  <p className="text-xs text-slate-500">Servicios a nombre del edificio (luz pasillos, agua, ascensores, etc.).</p>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={addServicio} data-testid="add-servicio-ac">
                  <Plus className="w-3.5 h-3.5 mr-1" />Agregar
                </Button>
              </div>

              {serviciosAC.length === 0 && (
                <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded p-4 text-center">
                  Sin servicios cargados. Hacé clic en &quot;Agregar&quot; para sumar uno.
                </div>
              )}

              {serviciosAC.map((s, idx) => (
                <div key={idx} className="border border-slate-200 rounded-md p-3 space-y-2 bg-slate-50/50" data-testid={`servicio-ac-row-${idx}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Servicio</Label>
                      <Input
                        data-testid={`servicio-ac-${idx}-servicio`}
                        value={s.servicio || ''}
                        onChange={(e) => updateServicio(idx, 'servicio', e.target.value)}
                        placeholder="Luz pasillos, Agua, Ascensor…"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">N° / NIC</Label>
                      <Input
                        data-testid={`servicio-ac-${idx}-numero`}
                        value={s.numero || ''}
                        onChange={(e) => updateServicio(idx, 'numero', e.target.value)}
                        placeholder="N° de cuenta o NIC"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Observaciones</Label>
                    <Input
                      data-testid={`servicio-ac-${idx}-observaciones`}
                      value={s.observaciones || ''}
                      onChange={(e) => updateServicio(idx, 'observaciones', e.target.value)}
                      placeholder="Notas (opcional)"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button" size="sm" variant="ghost"
                      className="text-rose-600 hover:bg-rose-50 h-7"
                      onClick={() => removeServicio(idx)}
                      data-testid={`servicio-ac-${idx}-remove`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />Quitar
                    </Button>
                  </div>
                </div>
              ))}
            </TabsContent>

            {/* ============== DOCUMENTACIÓN ============== */}
            <TabsContent value="documentacion" className="space-y-3 pt-4">
              {isTorreEmilia ? (
                <div className="space-y-3" data-testid="edificio-documentacion-section">
                  <p className="text-xs text-slate-500">Aplica a todas las unidades del edificio.</p>
                  <div className="rounded-md border border-slate-200 p-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-700">Escritura general</div>
                      <div className="text-xs mt-0.5">
                        {escrituraGen?.filename
                          ? <span className="text-emerald-700">Cargada · {escrituraGen.filename}</span>
                          : <span className="text-red-600">Falta cargar</span>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {escrituraGen?.filename && (
                        <>
                          <Button type="button" variant="outline" size="sm" onClick={handleDownload} data-testid="edif-doc-escritura-download">
                            <FileText className="w-3.5 h-3.5" />
                          </Button>
                          <Button type="button" variant="outline" size="sm" onClick={handleDelete} disabled={busy} className="text-rose-600" data-testid="edif-doc-escritura-delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="edif-doc-escritura-upload">
                        <Upload className="w-3.5 h-3.5 mr-1" />
                        {escrituraGen?.filename ? 'Reemplazar' : 'Cargar'}
                      </Button>
                      <input
                        ref={fileRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(e) => handleUpload(e.target.files?.[0])}
                        className="hidden"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded p-4 text-center">
                  Este edificio no tiene documentación específica a nivel edificio.
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsEditEdificioOpen(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="edif-save-btn">{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
